/**
 * Receipt Upload Component
 * 
 * Handles receipt image upload and displays extracted items
 */

import React, { useState } from 'react';
import { uploadReceipt, updateReceiptItems, deleteReceipt } from '../../services/api';
import { useAuth } from '../../context/authContext';

const ReceiptUpload = ({ onReceiptProcessed }) => {
    const { user } = useAuth();
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [processedReceipt, setProcessedReceipt] = useState(null);
    const [editingItems, setEditingItems] = useState(false);
    const [editedItems, setEditedItems] = useState([]);
    const [error, setError] = useState(null);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }
        
        setSelectedFile(file);
        setError(null);
        
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        if (!selectedFile || !user) return;
        
        try {
            setUploading(true);
            setError(null);
            const token = await user.getIdToken();
            const result = await uploadReceipt(selectedFile, token);
            setProcessedReceipt(result.receipt);
            setEditedItems(result.receipt.extracted_items || []);
            
            if (onReceiptProcessed) {
                onReceiptProcessed(result.receipt);
            }
        } catch (err) {
            console.error('Error uploading receipt:', err);
            setError(err.message || 'Failed to process receipt');
        } finally {
            setUploading(false);
        }
    };

    const handleSaveEdits = async () => {
        if (!processedReceipt || !user) return;
        
        try {
            setUploading(true);
            const token = await user.getIdToken();
            const result = await updateReceiptItems(processedReceipt.id, editedItems, token);
            setProcessedReceipt(result.receipt);
            setEditingItems(false);
        } catch (err) {
            console.error('Error updating receipt items:', err);
            setError(err.message || 'Failed to update receipt items');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteItem = (index) => {
        setEditedItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpdateItem = (index, field, value) => {
        setEditedItems(prev => prev.map((item, i) => 
            i === index ? { ...item, [field]: value } : item
        ));
    };

    const handleReset = () => {
        setSelectedFile(null);
        setPreview(null);
        setProcessedReceipt(null);
        setEditedItems([]);
        setEditingItems(false);
        setError(null);
    };

    return (
        <div className="bg-neutral-0 p-6 hairline">
            <h3 className="font-sans text-xl font-medium text-[#1b263b] mb-4">
                Upload Receipt
            </h3>
            
            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-600 text-red-900">
                    <p className="font-sans text-sm">{error}</p>
                </div>
            )}
            
            {!processedReceipt ? (
                <>
                    <div className="mb-4">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            className="hidden"
                            id="receipt-upload"
                        />
                        <label
                            htmlFor="receipt-upload"
                            className="block px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all cursor-pointer text-center"
                        >
                            Select Receipt Image
                        </label>
                    </div>
                    
                    {preview && (
                        <div className="mb-4">
                            <img
                                src={preview}
                                alt="Receipt preview"
                                className="max-w-full h-auto mb-4"
                            />
                            <button
                                onClick={handleUpload}
                                disabled={uploading}
                                className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none"
                                style={{ backgroundColor: '#1b263b', color: '#FFFFFF' }}
                                onMouseEnter={(e) => e.target.style.opacity = '0.6'}
                                onMouseLeave={(e) => e.target.style.opacity = '1'}
                            >
                                {uploading ? 'Processing...' : 'Process Receipt'}
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="font-sans text-lg font-medium text-[#1b263b]">
                            Extracted Items
                        </h4>
                        <div className="flex gap-2">
                            {editingItems ? (
                                <>
                                    <button
                                        onClick={handleSaveEdits}
                                        disabled={uploading}
                                        className="px-3 py-1 font-mono text-xs uppercase tracking-wider bg-[#1b263b] text-white border-none hover:opacity-60"
                                    >
                                        Save
                                    </button>
                                    <button
                                        onClick={() => {
                                            setEditingItems(false);
                                            setEditedItems(processedReceipt.extracted_items || []);
                                        }}
                                        className="px-3 py-1 font-mono text-xs uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0"
                                    >
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => {
                                        setEditingItems(true);
                                        setEditedItems([...processedReceipt.extracted_items || []]);
                                    }}
                                    className="px-3 py-1 font-mono text-xs uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0"
                                >
                                    Edit
                                </button>
                            )}
                            <button
                                onClick={handleReset}
                                className="px-3 py-1 font-mono text-xs uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0"
                            >
                                New Receipt
                            </button>
                        </div>
                    </div>
                    
                    <div className="space-y-2">
                        {(editingItems ? editedItems : processedReceipt.extracted_items || []).map((item, index) => (
                            <div
                                key={index}
                                className="p-3 bg-neutral-50 hairline flex items-center justify-between"
                            >
                                {editingItems ? (
                                    <>
                                        <div className="flex-1 grid grid-cols-3 gap-2">
                                            <input
                                                type="text"
                                                value={item.ingredient_name || ''}
                                                onChange={(e) => handleUpdateItem(index, 'ingredient_name', e.target.value)}
                                                className="px-2 py-1 bg-neutral-0 text-neutral-900 hairline font-sans text-sm"
                                                placeholder="Ingredient name"
                                            />
                                            <input
                                                type="number"
                                                value={item.quantity || ''}
                                                onChange={(e) => handleUpdateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                                className="px-2 py-1 bg-neutral-0 text-neutral-900 hairline font-sans text-sm"
                                                placeholder="Quantity"
                                            />
                                            <input
                                                type="text"
                                                value={item.unit || ''}
                                                onChange={(e) => handleUpdateItem(index, 'unit', e.target.value)}
                                                className="px-2 py-1 bg-neutral-0 text-neutral-900 hairline font-sans text-sm"
                                                placeholder="Unit"
                                            />
                                        </div>
                                        <button
                                            onClick={() => handleDeleteItem(index)}
                                            className="ml-2 px-2 py-1 font-mono text-xs uppercase tracking-wider bg-red-600 text-white border-none hover:opacity-60"
                                        >
                                            Delete
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div>
                                            <p className="font-sans text-sm font-medium text-neutral-900">
                                                {item.ingredient_name}
                                            </p>
                                            <p className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">
                                                {item.quantity} {item.unit}
                                            </p>
                                        </div>
                                        {item.confidence && (
                                            <span className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-40">
                                                {Math.round(item.confidence * 100)}% confidence
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                    
                    {processedReceipt.extracted_items && processedReceipt.extracted_items.length === 0 && (
                        <p className="font-sans text-sm text-neutral-900 opacity-60">
                            No items extracted from receipt. Please try uploading a clearer image or edit manually.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default ReceiptUpload;

